import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { safeGetItem, safeSetItem } from '@ais-ui/utils';
import { MainWindowActions, DataType } from '../modules/Popup/PopupEvents';

export type Theme = 'light' | 'dark' | 'system';
export type ResolvedTheme = 'light' | 'dark';

interface ThemeContextValue {
  theme: Theme;
  resolvedTheme: ResolvedTheme;
  setTheme: (theme: Theme) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

const STORAGE_KEY = 'ais-theme';

function getSystemTheme(): ResolvedTheme {
  if (typeof window === 'undefined') return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches
    ? 'dark'
    : 'light';
}

function getStoredTheme(): Theme {
  if (typeof window === 'undefined') return 'light';
  const stored = safeGetItem(STORAGE_KEY);
  if (stored === 'light' || stored === 'dark' || stored === 'system') {
    return stored;
  }
  return 'light'; // Default to light mode
}

interface ThemeProviderProps {
  children: ReactNode;
  /** Theme from server settings, if available */
  serverTheme?: Theme;
  /** Callback when theme changes, to sync to server */
  onThemeChange?: (theme: Theme) => void;
}

export const ThemeProvider = ({
  children,
  serverTheme,
  onThemeChange,
}: ThemeProviderProps) => {
  // Initialize from localStorage for instant load, then sync with server
  const [theme, setThemeState] = useState<Theme>(() => {
    const stored = getStoredTheme();
    // If server theme is provided and differs from local, prefer server
    if (serverTheme && serverTheme !== stored) {
      return serverTheme;
    }
    return stored;
  });

  const [systemTheme, setSystemTheme] = useState<ResolvedTheme>(getSystemTheme);

  // Sync with server theme when it becomes available
  useEffect(() => {
    if (serverTheme && serverTheme !== theme) {
      setThemeState(serverTheme);
      safeSetItem(STORAGE_KEY, serverTheme);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Only sync when serverTheme changes, not theme
  }, [serverTheme]);

  // Listen for system theme changes
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

    const handleChange = (e: MediaQueryListEvent) => {
      setSystemTheme(e.matches ? 'dark' : 'light');
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  const resolvedTheme: ResolvedTheme = useMemo(() => {
    return theme === 'system' ? systemTheme : theme;
  }, [theme, systemTheme]);

  // Apply theme class to document and broadcast to popups
  useEffect(() => {
    const root = document.documentElement;

    if (resolvedTheme === 'dark') {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }

    // Broadcast theme to popup windows
    MainWindowActions.sendDataUpdate(DataType.SETTINGS, { theme });
  }, [resolvedTheme, theme]);

  const setTheme = useCallback(
    (newTheme: Theme) => {
      setThemeState(newTheme);
      safeSetItem(STORAGE_KEY, newTheme);
      onThemeChange?.(newTheme);
    },
    [onThemeChange],
  );

  const value = useMemo(
    () => ({
      theme,
      resolvedTheme,
      setTheme,
    }),
    [theme, resolvedTheme, setTheme],
  );

  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
};

export const useThemeMode = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useThemeMode must be used within a ThemeProvider');
  }
  return context;
};
